"use client";

import { useCallback, useEffect, useState } from "react";

export type DropdownPlacement = "top" | "bottom";

export interface DropdownLayout {
  /** Max height (px) the box may occupy in its chosen placement. `undefined` until measured. */
  maxHeight?: number;
  /** Side the box should open toward, based on available viewport space. */
  placement: DropdownPlacement;
}

interface UseDropdownDismissOptions {
  boxRef: React.RefObject<HTMLElement | null>;
  triggerRef: React.RefObject<HTMLElement | null>;
  onClose: () => void;
}

/** Gap between trigger and box (mt-4 / mb-4 = 1rem). */
const BOX_GAP_PX = 16;
/** Breathing room kept against the viewport edge. */
const VIEWPORT_PADDING_PX = 8;

const DEFAULT_LAYOUT: DropdownLayout = { placement: "bottom" };

/**
 * Resolves how the box should open relative to its trigger: toward whichever
 * side has more room, capped to the space available there (never negative).
 */
function resolveLayout(
  triggerRect: DOMRect,
  viewportHeight: number,
): DropdownLayout {
  const reserved = BOX_GAP_PX + VIEWPORT_PADDING_PX;
  const spaceBelow = viewportHeight - triggerRect.bottom - reserved;
  const spaceAbove = triggerRect.top - reserved;
  const placement: DropdownPlacement =
    spaceBelow >= spaceAbove ? "bottom" : "top";
  const maxHeight = Math.max(
    0,
    placement === "bottom" ? spaceBelow : spaceAbove,
  );

  return { placement, maxHeight };
}

function isSameLayout(a: DropdownLayout, b: DropdownLayout): boolean {
  return a.placement === b.placement && a.maxHeight === b.maxHeight;
}

/**
 * Shared hook for dropdown/box dismiss behavior within the catalog feature.
 *
 * - Blocks page scroll while the box is open (body overflow hidden).
 * - Closes on click outside (mousedown outside box and trigger).
 * - Closes on Escape key, returning focus to the trigger.
 * - Measures available viewport space around the trigger and returns the
 *   `placement` (open down vs. flip up) plus a `maxHeight` cap so the box —
 *   including its pinned footer — never overflows the viewport. Recomputed on
 *   open and on resize/orientationchange. Body scroll is locked while open, so
 *   the trigger does not move and a single measurement per layout change holds.
 *
 * Pass `null` when the box is closed to skip all side effects.
 *
 * Used by QuickFilterDesktopBox and SortControlDesktop.
 */
export function useDropdownDismiss(
  options: UseDropdownDismissOptions | null,
): DropdownLayout {
  const onClose = options?.onClose;
  const [layout, setLayout] = useState<DropdownLayout>(DEFAULT_LAYOUT);

  const handleClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  useEffect(() => {
    if (!options) {
      setLayout(DEFAULT_LAYOUT);
      return;
    }

    const { boxRef, triggerRef } = options;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    function updateLayout() {
      const trigger = triggerRef.current;
      if (!trigger) return;

      const next = resolveLayout(
        trigger.getBoundingClientRect(),
        window.innerHeight,
      );

      // Callers pass a fresh options object each render, so this effect re-runs
      // every render; bail when nothing changed to avoid an endless state loop.
      setLayout((prev) => (isSameLayout(prev, next) ? prev : next));
    }

    updateLayout();

    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      const isInsideBox = boxRef.current?.contains(target);
      const isOnTrigger = triggerRef.current?.contains(target);

      if (!isInsideBox && !isOnTrigger) {
        handleClose();
      }
    }

    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        handleClose();
        triggerRef.current?.focus();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    window.addEventListener("resize", updateLayout);
    window.addEventListener("orientationchange", updateLayout);

    return () => {
      document.body.style.overflow = originalOverflow;
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
      window.removeEventListener("resize", updateLayout);
      window.removeEventListener("orientationchange", updateLayout);
    };
  }, [options, handleClose]);

  return layout;
}
